/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModalProps } from 'src/core/components/BasicModal';
import { ListModal } from 'src/core/components/ListModal';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { PrebuiltEntityRow } from 'src/luis/modules/entities/components/prebuiltEntitiesModal/PrebuiltEntityRow';
import { PrebuiltEntityMetadata } from 'src/luis/projects/entities/types/PrebuiltEntityMetadata';

export type PrebuiltEntitiesModalProps = BasicModalProps<PrebuiltModalDto>;

export type PrebuiltModalDto = {
    /**
     * The models that were selected in the modal.
     */
    selectedModels: PrebuiltEntityMetadata[];
};

const messages = defineMessages({
    prebuiltModalTitle: {
        id: 'entities.prebuiltEntitiesModal.prebuiltModalTitle',
        defaultMessage: 'Add prebuilt entities'
    },
    prebuiltModalSubtitle: {
        id: 'entities.prebuiltEntitiesModal.prebuiltModalSubtitle',
        defaultMessage: 'When you add a built-in entity, its predictions will be available to you while labeling utterances.'
    },
    searchPlaceholder: {
        id: 'entities.prebuiltEntitiesModal.prebuiltSearchPlaceholder',
        defaultMessage: 'Search built-in entities ...'
    },
    columnHeader: {
        id: 'entities.prebuiltEntitiesModal.prebuiltColumnHeader',
        defaultMessage: 'Name'
    },
    additionOperationCompleted: {
        id: 'entities.prebuiltEntitiesModal.additionOperationCompleted',
        defaultMessage: 'Entity successfully added'
    },
    addEntityToastStart: {
        id: 'entities.prebuiltEntitiesModal.addEntityToastStart',
        defaultMessage: 'Adding entity ...'
    },
    addEntityToastSuccess: {
        id: 'entities.prebuiltEntitiesModal.addEntityToastSuccess',
        defaultMessage: 'Add entity completed'
    },
    addEntityToastFailed: {
        id: 'entities.prebuiltEntitiesModal.addEntityToastFailed',
        defaultMessage: 'Add entity failed'
    }
});

/**
 * Displays a list modal with prebuilt entities in the list.
 *
 * @param props The list modal properties.
 */
export const PrebuiltEntitiesModal = withModalMounter(
    observer((props: PrebuiltEntitiesModalProps) => {
        const { onSubmit, ...rest } = props;
        const { localize } = useLocalization();
        const { rootStore, dispatcher } = useGlobalState();
        const { announcedComponent, announceAction } = useAnnounced();
        const { items, isLoading } = rootStore.currentLuisAppStore.prebuiltEntityStoreHouse;
        const { entityStoreHouse } = rootStore.currentLuisAppStore.currentAppVersionStore;
        const models = items.filter(item => !entityStoreHouse.findByName(item.name));

        React.useEffect(() => {
            dispatcher.dispatch('loadPrebuiltEntities');
        }, []);

        const addEntityTracker = useProgressTracker({
            onComplete: () => {
                announceAction(localize(messages.additionOperationCompleted));
                onSubmit();
            }
        });

        const addPrebuiltEntity = ({ selectedModels }: { selectedModels: PrebuiltEntityMetadata[] }) => {
            dispatcher.dispatch('addPrebuiltEntities', {
                entityNames: selectedModels.map(m => m.name),
                tracker: addEntityTracker,
                toastData: {
                    running: { title: localize(messages.addEntityToastStart) },
                    completed: { title: localize(messages.addEntityToastSuccess) },
                    failed: { title: localize(messages.addEntityToastFailed) }
                }
            });
        };

        return (
            <>
                <ListModal<PrebuiltEntityMetadata>
                    items={models}
                    searchKeyParam="name"
                    isLoading={isLoading}
                    disabled={addEntityTracker.isRunning}
                    columnHeader={localize(messages.columnHeader)}
                    subtitle={localize(messages.prebuiltModalSubtitle)}
                    searchPlaceholder={localize(messages.searchPlaceholder)}
                    onColumnRender={item => <PrebuiltEntityRow {...item} />}
                    dialogContentProps={{ title: localize(messages.prebuiltModalTitle) }}
                    onSubmit={dto => addPrebuiltEntity({ selectedModels: dto.selectedItems })}
                    {...rest}
                />
                {announcedComponent}
            </>
        );
    })
);
